// Dialogs for the Project Datasheet Composer's Catalog tab. STUI popup skeleton throughout
// (ConfirmModal from the master Catalog tab supplies it for the yes/no dialogs).
import { X } from 'lucide-react';
import { ConfirmModal } from './CatalogModals';
import { manufacturerText, datasheetText, summarizePull } from './projectCatalog';

/** What a pull will do, sheet by sheet, before anything is written. */
export function PullPreviewModal({ sourceLabel, rows, bringValues, busy = false, onPull, onClose }) {
  return (
    <ConfirmModal title={`Pull ${rows.length} Index Sheet${rows.length === 1 ? '' : 's'} from ${sourceLabel}`}
      confirmLabel="Pull" busy={busy} onConfirm={onPull} onClose={onClose}>
      <p className="stui-hint">
        Each sheet is added to this database in its own step: one that fails does not undo the others.
        {bringValues ? ' Datasheet values come along.' : ' Datasheet values are left behind.'}
      </p>
      <div className="stui-data-grid-container">
        <div className="stui-data-grid-scroll-area">
          <table className="stui-data-grid stui-data-grid-auto">
            <thead>
              <tr><th>Index Sheet</th><th>Manufacturer</th><th>Status here</th><th>Datasheet</th></tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.sourceId}>
                  <td className="stui-mono">{r.indexSheet}</td>
                  <td>{manufacturerText(r)}</td>
                  <td>{r.landingStatus || ''}{r.landingStatus !== r.status && r.status ? ` (was ${r.status})` : ''}</td>
                  <td>{datasheetText(r, bringValues)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </ConfirmModal>
  );
}

/** Per-sheet results of a pull: pulled, skipped or failed, with the reason and any warnings. */
export function PullResultsModal({ results, onClose }) {
  const n = summarizePull(results);
  return (
    <div className="stui-modal-overlay">
      <div className="stui-modal stui-modal-md">
        <div className="stui-modal-header">
          <span className="stui-modal-title">Pull results</span>
          <button className="stui-modal-close" onClick={onClose} title="Close"><X size={20} /></button>
        </div>
        <div className="stui-modal-body">
          <p className="stui-hint">{`${n.pulled} pulled, ${n.skipped} skipped, ${n.failed} failed.`}</p>
          <div className="stui-data-grid-container">
            <div className="stui-data-grid-scroll-area">
              <table className="stui-data-grid stui-data-grid-auto">
                <thead><tr><th>Index Sheet</th><th>Result</th><th>Details</th></tr></thead>
                <tbody>
                  {results.map(r => (
                    <tr key={r.sourceId}>
                      <td className="stui-mono">{r.indexSheet ?? `#${r.sourceId}`}</td>
                      <td>
                        <span className={`stui-badge ${r.status === 'pulled' ? 'stui-badge-green' : r.status === 'skipped' ? 'stui-badge-gray' : 'stui-badge-red'}`}>{r.status}</span>
                      </td>
                      <td>
                        {r.status === 'pulled'
                          ? [
                            r.datasheet ? `${r.datasheet}${r.valuesCopied ? `, ${r.valuesCopied} values` : ''}` : 'No datasheet',
                            r.manufacturer?.action === 'Create' ? 'manufacturer created' : null,
                            ...(r.warnings ?? []),
                          ].filter(Boolean).join('; ')
                          : r.reason}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div className="stui-modal-footer">
          <button className="stui-btn stui-btn-primary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

/** "Update from source": the fields that change on this database's sheet. The datasheet never does. */
export function UpdateFromSourceModal({ row, sourceLabel, busy = false, onUpdate, onClose }) {
  return (
    <ConfirmModal title={`Update ${row.indexSheet} from ${sourceLabel}`} confirmLabel="Update" busy={busy}
      onConfirm={onUpdate} onClose={onClose}>
      <div className="stui-data-grid-container stui-mb-md">
        <div className="stui-data-grid-scroll-area">
          <table className="stui-data-grid stui-data-grid-auto">
            <thead><tr><th>Field</th><th>Here</th><th>{sourceLabel}</th></tr></thead>
            <tbody>
              {(row.fieldChanges ?? []).map(c => (
                <tr key={c.field}><td>{c.field}</td><td>{c.target ?? ''}</td><td>{c.source ?? ''}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {row.fieldChanges?.some(c => c.field === 'Manufacturer') && (
        <p className="stui-hint">{`Manufacturer: ${manufacturerText(row)}.`}</p>
      )}
      <p className="stui-hint">The sheet's datasheet is not changed. A different datasheet comes over from the difference viewer.</p>
    </ConfirmModal>
  );
}
